/**
 * Lotgenootje blog content processor — transforms article.content into styled components.
 * Markers use [PREFIX] in <p> tags (Shopify HTML view). Styling matches if_article_master blocks.
 */

const LotgenBlogUtils = {
  slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  },

  stripHtml(html) {
    const el = document.createElement('div');
    el.innerHTML = html;
    return el.textContent || el.innerText || '';
  },

  splitPipe(line) {
    return line
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
  },

  getInitials(name) {
    if (!name) return 'MJ';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'MJ';
  },

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  uniqueId(prefix) {
    return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
  },

  MARKER_CLASS_MAP: {
    'lotgen-thought': 'THOUGHT',
    'lotgen-expert-tip': 'EXPERT-TIP',
    'lotgen-stat-insight': 'STAT-INSIGHT',
    'lotgen-stat': 'STAT',
    'lotgen-cta-title': 'CTA-TITLE',
    'lotgen-cta-desc': 'CTA-DESC',
    'lotgen-cta-link': 'CTA-LINK',
    'lotgen-cta-btn': 'CTA-BTN',
    'lotgen-author-role': 'AUTHOR-ROLE',
    'lotgen-author-name': 'AUTHOR-NAME',
    'lotgen-faq': 'FAQ',
    'lotgen-faq-eyebrow': 'FAQ-EYEBROW',
    'lotgen-faq-link': 'FAQ-LINK',
    'lotgen-ai-share': 'AI-SHARE',
    'lotgen-collapsible': 'COLLAPSIBLE',
  },

  parseMarker(el) {
    if (!el || el.tagName !== 'P') return null;

    for (const [className, prefix] of Object.entries(LotgenBlogUtils.MARKER_CLASS_MAP)) {
      if (el.classList.contains(className)) {
        return { prefix, content: (el.textContent || '').trim(), element: el };
      }
    }

    const text = (el.textContent || '').trim();
    const match = text.match(/^\[([A-Z][A-Z0-9-]*)\]\s*([\s\S]*)$/);
    if (match) {
      return { prefix: match[1], content: match[2].trim(), element: el };
    }

    return null;
  },

  parseTitleBody(content, defaultTitle) {
    const parts = LotgenBlogUtils.splitPipe(content);
    if (parts.length >= 2) {
      return { title: parts[0], body: parts.slice(1).join(' | ') };
    }
    return { title: defaultTitle, body: content };
  },

  insertComponent(element, html) {
    const trimmed = html.trim();
    if (!trimmed) {
      element.remove();
      return;
    }

    const wrap = document.createElement('div');
    wrap.innerHTML = trimmed;
    const node = wrap.firstElementChild;
    if (node) element.replaceWith(node);
    else element.remove();
  },

  removeElements(elements) {
    elements.forEach((el) => el.remove());
  },

  isEmptyParagraph(el) {
    if (!el || el.tagName !== 'P') return false;
    const html = el.innerHTML.replace(/&nbsp;/gi, ' ').replace(/<br\s*\/?>/gi, '').trim();
    const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
    return !text && !html;
  },

  removeEmptyParagraphs(container) {
    container.querySelectorAll('p').forEach((paragraph) => {
      if (LotgenBlogUtils.isEmptyParagraph(paragraph)) paragraph.remove();
    });
  },

  parseStatLine(text) {
    const parts = LotgenBlogUtils.splitPipe(text.trim());
    if (parts.length < 2) return null;
    return { value: parts[0], label: parts.slice(1).join(' | ') };
  },

  buildThought(text) {
    const id = LotgenBlogUtils.uniqueId('lotgen_thought');
    return `
      <div class="block_thought lotgen-blog-thought">
        <div class="thought_text" id="${id}">
          <p>${LotgenBlogUtils.escapeHtml(text)}</p>
        </div>
        <button type="button" class="copy-btn" data-copy-target="${id}">Kopieer naar klembord</button>
      </div>
    `;
  },

  buildExpertTip(content) {
    const { title, body } = LotgenBlogUtils.parseTitleBody(content, 'Expert tip');
    return `
      <div class="block_expert_tip lotgen-blog-expert-tip">
        <div class="expert_tip_content">
          <h3 class="tip_title">${LotgenBlogUtils.escapeHtml(title)}</h3>
          <div class="tip_text"><p>${LotgenBlogUtils.escapeHtml(body)}</p></div>
        </div>
      </div>
    `;
  },

  buildStatInsight(content) {
    const { title, body } = LotgenBlogUtils.parseTitleBody(content, 'Statistisch inzicht');
    return `
      <div class="block_statistical_insight lotgen-blog-stat-insight">
        <div class="statistical_content">
          <h3 class="statistical__title">${LotgenBlogUtils.escapeHtml(title)}</h3>
          <div class="statistical__text"><p>${LotgenBlogUtils.escapeHtml(body)}</p></div>
        </div>
      </div>
    `;
  },

  buildStatsCard(entries) {
    if (!entries.length) return '';
    const items = entries
      .slice(0, 3)
      .map(
        (entry) => `
        <div class="lotgen-blog-stat-item">
          <div class="lotgen-blog-stat-value">${LotgenBlogUtils.escapeHtml(entry.value)}</div>
          <div class="lotgen-blog-stat-label">${LotgenBlogUtils.escapeHtml(entry.label)}</div>
        </div>
      `
      )
      .join('');

    return `<div class="lotgen-blog-stats"><div class="lotgen-blog-stats__grid">${items}</div></div>`;
  },

  buildInlineCta(parts) {
    if (parts.length < 4) return '';
    const [title, desc, url, btnText] = parts;
    return `
      <div class="lotgen-blog-inline-cta">
        <div class="lotgen-blog-inline-cta__title">${LotgenBlogUtils.escapeHtml(title)}</div>
        <div class="lotgen-blog-inline-cta__text">${LotgenBlogUtils.escapeHtml(desc)}</div>
        <a href="${LotgenBlogUtils.escapeHtml(url)}" class="lotgen-blog-inline-cta__btn">${LotgenBlogUtils.escapeHtml(btnText)}</a>
      </div>
    `;
  },

  buildAiShare(content) {
    const { title, body: prompt } = LotgenBlogUtils.parseTitleBody(content, 'Deel dit artikel met AI');
    const id = LotgenBlogUtils.uniqueId('lotgen_ai');
    const encodedPrompt = encodeURIComponent(prompt);
    const pageUrl = encodeURIComponent(window.location.href);

    return `
      <div class="lotgen-blog-ai-share" data-ai-share="${id}">
        <div class="lotgen-blog-ai-share__title">${LotgenBlogUtils.escapeHtml(title)}</div>
        <div class="lotgen-blog-ai-share__buttons">
          <a href="https://chat.openai.com/?q=${encodedPrompt}%20${pageUrl}" target="_blank" rel="noopener" class="lotgen-blog-ai-share__btn lotgen-blog-ai-share__btn--chatgpt">ChatGPT</a>
          <a href="https://www.perplexity.ai/search/new?q=${encodedPrompt}%20${pageUrl}" target="_blank" rel="noopener" class="lotgen-blog-ai-share__btn lotgen-blog-ai-share__btn--perplexity">Perplexity</a>
          <a href="https://claude.ai/new?q=${encodedPrompt}%20${pageUrl}" target="_blank" rel="noopener" class="lotgen-blog-ai-share__btn lotgen-blog-ai-share__btn--claude">Claude</a>
        </div>
      </div>
    `;
  },

  parseFaqLine(text) {
    const line = text.trim();
    const pipeIndex = line.indexOf('|');
    if (pipeIndex === -1) return null;
    const question = line.slice(0, pipeIndex).trim();
    const answer = line.slice(pipeIndex + 1).trim();
    if (!question || !answer) return null;
    return { question, answer };
  },

  parseFaqLink(text) {
    const parts = LotgenBlogUtils.splitPipe(text.trim());
    if (parts.length < 2) return null;
    return { url: parts[0], text: parts.slice(1).join(' | ') };
  },

  buildFaqSection({ eyebrow, title, items, linkUrl, linkText }) {
    const itemsHtml = items
      .map(
        (item, index) => `
        <div class="lotgen-blog-collapsible-item${index === 0 ? ' is-open' : ''}">
          <button type="button" class="lotgen-blog-collapsible-head" aria-expanded="${index === 0 ? 'true' : 'false'}">
            <span class="lotgen-blog-collapsible-title">${LotgenBlogUtils.escapeHtml(item.question)}</span>
            <span class="lotgen-blog-collapsible-icon" aria-hidden="true">
              <svg class="icon-plus" xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none"><path d="M8.5 1.5V16.5" stroke="#A2765D" stroke-width="2" stroke-linecap="round"></path><path d="M1 9L16 9" stroke="#A2765D" stroke-width="2" stroke-linecap="round"></path></svg>
              <svg class="icon-minus" xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none"><path d="M1 8L16 8" stroke="#A2765D" stroke-width="2" stroke-linecap="round"></path></svg>
            </span>
          </button>
          <div class="lotgen-blog-collapsible-body">
            <div class="lotgen-blog-collapsible-content"><p>${LotgenBlogUtils.escapeHtml(item.answer)}</p></div>
          </div>
        </div>
      `
      )
      .join('');

    const eyebrowHtml = eyebrow
      ? `<p class="lotgen-blog-faq-eyebrow">${LotgenBlogUtils.escapeHtml(eyebrow)}</p>`
      : '';
    const linkHtml =
      linkUrl && linkText
        ? `<a href="${LotgenBlogUtils.escapeHtml(linkUrl)}" class="lotgen-blog-faq-link">${LotgenBlogUtils.escapeHtml(linkText)}</a>`
        : '';

    return `
      <div class="lotgen-blog-faq-section">
        ${eyebrowHtml}
        <h2 class="lotgen-blog-faq-title">${LotgenBlogUtils.escapeHtml(title)}</h2>
        <div class="lotgen-blog-collapsible-group">${itemsHtml}</div>
        ${linkHtml}
      </div>
    `;
  },

  bindCopyButtons(container) {
    container.querySelectorAll('[data-copy-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.getElementById(btn.getAttribute('data-copy-target') || '');
        const text = target?.textContent?.trim() || '';
        navigator.clipboard?.writeText(text).then(() => {
          const original = btn.textContent;
          btn.textContent = 'Gekopieerd!';
          setTimeout(() => {
            btn.textContent = original;
          }, 2000);
        });
      });
    });
  },

  bindCollapsibleDelegation(container) {
    if (container.dataset.collapsibleBound === 'true') return;
    container.dataset.collapsibleBound = 'true';

    container.addEventListener('click', (event) => {
      const trigger = event.target.closest('.lotgen-blog-collapsible-head');
      if (!trigger || !container.contains(trigger)) return;

      event.preventDefault();
      const item = trigger.closest('.lotgen-blog-collapsible-item');
      const group = trigger.closest('.lotgen-blog-collapsible-group');
      if (!item || !group) return;

      const isOpen = item.classList.contains('is-open');
      group.querySelectorAll('.lotgen-blog-collapsible-item').forEach((other) => {
        other.classList.remove('is-open');
        other.querySelector('.lotgen-blog-collapsible-head')?.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  },

  initCollapsibles(container) {
    LotgenBlogUtils.bindCollapsibleDelegation(container);
  },
};

const LotgenBlogCleanupHandler = {
  name: 'cleanup',
  run(container) {
    LotgenBlogUtils.removeEmptyParagraphs(container);
  },
};

const LotgenBlogPrefixHandler = {
  name: 'prefix-markers',

  run(container, context) {
    let changed = true;

    while (changed) {
      changed = false;
      const paragraphs = [...container.querySelectorAll('p')];

      for (let i = 0; i < paragraphs.length; i += 1) {
        const marker = LotgenBlogUtils.parseMarker(paragraphs[i]);
        if (!marker) continue;

        if (marker.prefix === 'AUTHOR-ROLE') {
          context.authorRoleOverride = marker.content;
          marker.element.remove();
          changed = true;
          break;
        }

        if (marker.prefix === 'AUTHOR-NAME') {
          context.authorNameOverride = marker.content;
          marker.element.remove();
          changed = true;
          break;
        }

        if (marker.prefix === 'THOUGHT') {
          LotgenBlogUtils.insertComponent(marker.element, LotgenBlogUtils.buildThought(marker.content));
          changed = true;
          break;
        }

        if (marker.prefix === 'EXPERT-TIP') {
          LotgenBlogUtils.insertComponent(marker.element, LotgenBlogUtils.buildExpertTip(marker.content));
          changed = true;
          break;
        }

        if (marker.prefix === 'STAT-INSIGHT') {
          LotgenBlogUtils.insertComponent(marker.element, LotgenBlogUtils.buildStatInsight(marker.content));
          changed = true;
          break;
        }

        if (marker.prefix === 'AI-SHARE') {
          LotgenBlogUtils.insertComponent(marker.element, LotgenBlogUtils.buildAiShare(marker.content));
          changed = true;
          break;
        }

        if (marker.prefix === 'STAT') {
          const group = [marker];
          let j = i + 1;
          while (j < paragraphs.length) {
            const next = LotgenBlogUtils.parseMarker(paragraphs[j]);
            if (next?.prefix !== 'STAT') break;
            group.push(next);
            j += 1;
          }

          const entries = group
            .map((item) => LotgenBlogUtils.parseStatLine(item.content))
            .filter(Boolean);

          LotgenBlogUtils.insertComponent(group[0].element, LotgenBlogUtils.buildStatsCard(entries));
          LotgenBlogUtils.removeElements(group.slice(1).map((item) => item.element));
          changed = true;
          break;
        }

        if (marker.prefix === 'CTA-TITLE') {
          const desc = LotgenBlogUtils.parseMarker(paragraphs[i + 1]);
          const link = LotgenBlogUtils.parseMarker(paragraphs[i + 2]);
          const btn = LotgenBlogUtils.parseMarker(paragraphs[i + 3]);

          if (
            desc?.prefix !== 'CTA-DESC' ||
            link?.prefix !== 'CTA-LINK' ||
            btn?.prefix !== 'CTA-BTN'
          ) {
            continue;
          }

          LotgenBlogUtils.insertComponent(
            marker.element,
            LotgenBlogUtils.buildInlineCta([
              marker.content,
              desc.content,
              link.content,
              btn.content,
            ])
          );
          LotgenBlogUtils.removeElements([desc.element, link.element, btn.element]);
          changed = true;
          break;
        }

        if (['CTA-DESC', 'CTA-LINK', 'CTA-BTN', 'COLLAPSIBLE'].includes(marker.prefix)) {
          continue;
        }
      }
    }

    LotgenBlogUtils.bindCopyButtons(container);
  },
};

const LotgenBlogTableHandler = {
  name: 'tables',
  run(container) {
    container.querySelectorAll('table').forEach((table) => {
      table.classList.add('lotgen-blog-table');

      if (!table.parentElement?.classList.contains('lotgen-rte-table-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'lotgen-rte-table-wrapper';
        table.parentNode?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    });
  },
};

const LotgenBlogQuoteHandler = {
  name: 'quotes',
  run(container) {
    container.querySelectorAll('blockquote').forEach((blockquote) => {
      blockquote.classList.add('lotgen-blog-quote');
    });
  },
};

const LotgenBlogFaqHandler = {
  name: 'faq',

  run(container) {
    const headings = [...container.querySelectorAll('h2')].filter(
      (heading) => !heading.closest('.lotgen-blog-faq-section, .lotgen-blog-author-block')
    );

    headings.forEach((heading) => {
      const title = heading.textContent?.trim() || '';
      let eyebrow = '';

      const previous = heading.previousElementSibling;
      const previousMarker =
        previous?.tagName === 'P' ? LotgenBlogUtils.parseMarker(previous) : null;

      if (previousMarker?.prefix === 'FAQ-EYEBROW') {
        eyebrow = previousMarker.content;
        previous.remove();
      }

      const faqItems = [];
      let linkData = null;
      let node = heading.nextElementSibling;

      while (node) {
        if (['H2', 'H3'].includes(node.tagName)) break;

        const marker = node.tagName === 'P' ? LotgenBlogUtils.parseMarker(node) : null;

        if (marker && (marker.prefix === 'FAQ' || marker.prefix === 'COLLAPSIBLE')) {
          const item = LotgenBlogUtils.parseFaqLine(marker.content);
          if (item) faqItems.push(item);
          const next = node.nextElementSibling;
          node.remove();
          node = next;
          continue;
        }

        if (marker?.prefix === 'FAQ-LINK' && faqItems.length) {
          linkData = LotgenBlogUtils.parseFaqLink(marker.content);
          const next = node.nextElementSibling;
          node.remove();
          node = next;
          continue;
        }

        if (faqItems.length) break;
        node = node.nextElementSibling;
      }

      if (!faqItems.length) return;

      const section = document.createElement('div');
      section.innerHTML = LotgenBlogUtils.buildFaqSection({
        eyebrow,
        title,
        items: faqItems,
        linkUrl: linkData?.url,
        linkText: linkData?.text,
      });

      const sectionEl = section.firstElementChild;
      const sectionHeading = sectionEl?.querySelector('.lotgen-blog-faq-title');
      if (heading.id && sectionHeading) sectionHeading.id = heading.id;

      heading.replaceWith(sectionEl);
    });
  },
};

const LotgenBlogAuthorHandler = {
  name: 'author-bio',

  run(container, context) {
    const headings = [...container.querySelectorAll('h2')];

    headings.forEach((heading) => {
      const text = heading.textContent?.trim() || '';
      if (!/over de auteur|about the author|geschreven door/i.test(text)) return;

      const authorName = context.authorNameOverride || context.authorName || 'Marien Jansen';
      let role = context.authorRoleOverride || context.authorRole || 'Oprichter van Lotgenootje';
      const bioNodes = [];
      let node = heading.nextElementSibling;

      if (!context.authorRoleOverride && node?.tagName === 'P' && !LotgenBlogUtils.parseMarker(node)) {
        const roleText = node.textContent?.trim() || '';
        if (roleText) role = roleText;
        node = node.nextElementSibling;
      }

      while (node && !['H2', 'H3'].includes(node.tagName)) {
        const next = node.nextElementSibling;
        bioNodes.push(node);
        node = next;
      }

      const section = document.createElement('div');
      section.className = 'block_author_block lotgen-blog-author-block';
      section.innerHTML = `
        <div class="author-img">
          <div class="author-img__initials">${LotgenBlogUtils.getInitials(authorName)}</div>
        </div>
        <div class="author-content">
          <p class="author-written">Geschreven door</p>
          <h3 class="author-name">${LotgenBlogUtils.escapeHtml(authorName)}</h3>
          <p class="author-role">${LotgenBlogUtils.escapeHtml(role)}</p>
          <div class="author-bio"></div>
        </div>
      `;

      const bioEl = section.querySelector('.author-bio');
      bioNodes.forEach((n) => bioEl?.appendChild(n));

      const articlesUrl = context.authorArticlesUrl || '';
      if (articlesUrl && bioEl) {
        const firstName = authorName.trim().split(/\s+/)[0] || authorName;
        const link = document.createElement('a');
        link.href = articlesUrl;
        link.className = 'lotgen-blog-author-link';
        link.textContent = `Bekijk alle artikelen van ${firstName} →`;
        bioEl.appendChild(link);
      }

      heading.replaceWith(section);
    });
  },
};

const LotgenBlogTocHandler = {
  name: 'toc',

  run(container) {
    const tocList = document.getElementById('lotgenBlogTocList');
    if (!tocList) return;

    const headings = [...container.querySelectorAll('h2')].filter(
      (h) => !h.closest('.lotgen-blog-author-block')
    );

    const usedIds = new Set();
    const items = [];

    headings.forEach((heading) => {
      const label = heading.textContent?.trim() || '';
      if (!label) return;

      let id = heading.id || LotgenBlogUtils.slugify(label);
      let counter = 2;
      while (usedIds.has(id)) {
        id = `${LotgenBlogUtils.slugify(label)}-${counter}`;
        counter += 1;
      }
      usedIds.add(id);
      heading.id = id;
      items.push({ id, label });
    });

    if (!items.length) {
      tocList.closest('.lotgen-blog-details-toc')?.classList.add('hidden');
      return;
    }

    tocList.innerHTML = items
      .map(
        (item, index) => `
        <li class="lotgen-blog-details-toc__item${index === 0 ? ' active' : ''}">
          <a href="#${item.id}">${LotgenBlogUtils.escapeHtml(item.label)}</a>
        </li>
      `
      )
      .join('');

    const tocLinks = tocList.querySelectorAll('a');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          tocLinks.forEach((link) => link.parentElement?.classList.remove('active'));
          tocList.querySelector(`a[href="#${entry.target.id}"]`)?.parentElement?.classList.add('active');
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    headings.forEach((h) => observer.observe(h));

    tocLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        document.querySelector(link.getAttribute('href'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  },
};

class LotgenBlogContentProcessor extends HTMLElement {
  connectedCallback() {
    const container = this.querySelector('[data-blog-content-body]');
    if (!container) return;

    const context = {
      authorName: this.dataset.articleAuthor || '',
      authorRole: this.dataset.articleAuthorRole || '',
      authorArticlesUrl: this.dataset.authorArticlesUrl || '',
    };

    const handlers = [
      LotgenBlogCleanupHandler,
      LotgenBlogPrefixHandler,
      LotgenBlogQuoteHandler,
      LotgenBlogTableHandler,
      LotgenBlogFaqHandler,
      LotgenBlogTocHandler,
      LotgenBlogAuthorHandler,
    ];

    handlers.forEach((handler) => {
      try {
        handler.run(container, context);
      } catch (error) {
        console.warn(`[LotgenBlogContentProcessor] ${handler.name} failed:`, error);
      }
    });

    LotgenBlogUtils.initCollapsibles(container);
  }
}

if (!customElements.get('lotgen-blog-content-processor')) {
  customElements.define('lotgen-blog-content-processor', LotgenBlogContentProcessor);
}
